'use client';

import * as React from 'react';
import { cn } from '../lib/utils';

/**
 * URL pair for AVIF/WebP (matches SDK MediaUrlPair)
 */
export interface MediaImageUrls {
  /** AVIF URL (primary, best compression) */
  avifUrl: string;
  /** WebP URL (fallback for older browsers) */
  webpUrl: string;
}

/**
 * Size presets for MediaImage
 */
export type MediaImageSize = 'thumb' | 'display' | 'detail' | 'custom';

/**
 * Props for MediaImage component
 */
export interface MediaImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** URL pair with avifUrl and webpUrl */
  urls?: MediaImageUrls | null;
  /** Placeholder background color (hex) for LQIP */
  placeholderColor?: string | null;
  /** Alt text (required for accessibility) */
  alt: string;
  /** Size preset - affects default dimensions */
  size?: MediaImageSize;
  /** Custom width (overrides size preset) */
  width?: number | string;
  /** Custom height (overrides size preset) */
  height?: number | string;
  /** Fill container (object-fit: cover) */
  fill?: boolean;
  /** Object fit mode */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  /** Object position */
  objectPosition?: string;
  /** Enable lazy loading (default: true) */
  lazy?: boolean;
  /** Callback when image loads */
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  /** Callback when image fails to load */
  onError?: React.ReactEventHandler<HTMLImageElement>;
  /** Prefetch the image (useful for images below fold) */
  prefetch?: boolean;
  /** Container className */
  containerClassName?: string;
}

/**
 * Size preset dimensions (in pixels)
 */
const SIZE_PRESETS: Record<Exclude<MediaImageSize, 'custom'>, { width: number; height: number }> = {
  thumb: { width: 64, height: 64 },
  display: { width: 256, height: 256 },
  detail: { width: 512, height: 512 },
};

/**
 * MediaImage - Optimized image component with AVIF/WebP support and LQIP placeholder
 *
 * Features:
 * - Uses `<picture>` element with AVIF primary, WebP fallback
 * - Shows placeholder color background while loading (LQIP)
 * - Native lazy loading support
 * - Smooth fade-in transition on load
 * - Accessible with required alt text
 *
 * @example
 * ```tsx
 * import { MediaImage } from '@makanly/ui-kit';
 *
 * // Basic usage
 * <MediaImage
 *   urls={asset.urls?.thumb}
 *   placeholderColor={asset.placeholderColor}
 *   alt="Nasi Lemak"
 *   size="thumb"
 * />
 *
 * // Fill container
 * <MediaImage
 *   urls={asset.urls?.display}
 *   placeholderColor={asset.placeholderColor}
 *   alt="Hero image"
 *   fill
 *   objectFit="cover"
 * />
 * ```
 */
const MediaImage = React.forwardRef<HTMLImageElement, MediaImageProps>(
  (
    {
      urls,
      placeholderColor,
      alt,
      size = 'display',
      width,
      height,
      fill = false,
      objectFit = 'cover',
      objectPosition = 'center',
      lazy = true,
      onLoad,
      onError,
      prefetch = false,
      className,
      containerClassName,
      style,
      ...props
    },
    ref,
  ) => {
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);

    // Get dimensions from size preset or custom values
    const dimensions = React.useMemo(() => {
      if (fill) return undefined;
      if (width !== undefined || height !== undefined) {
        return { width, height };
      }
      if (size !== 'custom') {
        return SIZE_PRESETS[size];
      }
      return undefined;
    }, [fill, width, height, size]);

    // Handle load event
    const handleLoad = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        setIsLoaded(true);
        onLoad?.(event);
      },
      [onLoad],
    );

    // Handle error event
    const handleError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        setHasError(true);
        onError?.(event);
      },
      [onError],
    );

    // Prefetch effect
    React.useEffect(() => {
      if (!prefetch || !urls) {
        return;
      }

      // Prefetch AVIF
      const linkAvif = document.createElement('link');
      linkAvif.rel = 'prefetch';
      linkAvif.as = 'image';
      linkAvif.href = urls.avifUrl;
      linkAvif.type = 'image/avif';
      document.head.appendChild(linkAvif);

      // Prefetch WebP as fallback
      const linkWebp = document.createElement('link');
      linkWebp.rel = 'prefetch';
      linkWebp.as = 'image';
      linkWebp.href = urls.webpUrl;
      linkWebp.type = 'image/webp';
      document.head.appendChild(linkWebp);

      return () => {
        document.head.removeChild(linkAvif);
        document.head.removeChild(linkWebp);
      };
    }, [prefetch, urls]);

    // No URLs provided - show placeholder
    if (!urls) {
      return (
        <div
          className={cn(
            'flex items-center justify-center bg-muted text-muted-foreground',
            fill && 'absolute inset-0',
            containerClassName,
          )}
          style={{
            width: fill ? undefined : dimensions?.width,
            height: fill ? undefined : dimensions?.height,
            backgroundColor: placeholderColor || undefined,
            ...style,
          }}
          aria-label={alt}
          role="img"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-1/3 w-1/3 opacity-50"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
      );
    }

    // Container styles
    const containerStyles: React.CSSProperties = {
      backgroundColor: placeholderColor || '#f5f5f5',
      width: fill ? undefined : dimensions?.width,
      height: fill ? undefined : dimensions?.height,
      position: fill ? 'absolute' : 'relative',
      inset: fill ? 0 : undefined,
      overflow: 'hidden',
      ...style,
    };

    // Image styles
    const imageStyles: React.CSSProperties = {
      objectFit,
      objectPosition,
      opacity: isLoaded ? 1 : 0,
      transition: 'opacity 200ms ease-in-out',
      width: fill ? '100%' : dimensions?.width,
      height: fill ? '100%' : dimensions?.height,
    };

    return (
      <div className={cn('relative', containerClassName)} style={containerStyles}>
        <picture>
          <source srcSet={urls.avifUrl} type="image/avif" />
          <img
            ref={ref}
            src={urls.webpUrl}
            alt={alt}
            loading={lazy ? 'lazy' : 'eager'}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            className={cn(
              'block',
              hasError && 'hidden',
              fill && 'absolute inset-0 h-full w-full',
              className,
            )}
            style={imageStyles}
            {...(dimensions && !fill
              ? {
                  width: dimensions.width,
                  height: dimensions.height,
                }
              : {})}
            {...props}
          />
        </picture>

        {/* Error fallback */}
        {hasError && (
          <div
            className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground"
            role="img"
            aria-label={`Failed to load: ${alt}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-1/3 w-1/3 opacity-50"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="15" />
              <line x1="15" y1="9" x2="9" y2="15" />
            </svg>
          </div>
        )}
      </div>
    );
  },
);

MediaImage.displayName = 'MediaImage';

export { MediaImage };
